import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, CheckCircle, XCircle, Clock, Trash2 } from 'lucide-react';

const ProgramChangeRequests = () => {
  const { hasRole } = useAuth();

  // Legacy module is intentionally frozen during canonical migration.
  const canApprove = false;
  const canCreate = false;

  const [requests, setRequests] = useState([]);
  const [coilIns, setCoilIns] = useState([]);
  const [programs, setPrograms] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);

  const [selectedRequest, setSelectedRequest] = useState(null);

  const [formData, setFormData] = useState({
    coil_in_id: '',
    coil_id: '',
    current_program_id: '',
    proposed_circle_sizes: [''],
    proposed_rate_per_kg: '',
    reason: '',
  });

  const [approvalData, setApprovalData] = useState({
    status: 'APPROVED',
    notes: '',
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
    fetchCoilIns();
  }, []);

  const selectedCoilInDoc = useMemo(() => {
    return (coilIns || []).find((c) => c.id === formData.coil_in_id) || null;
  }, [coilIns, formData.coil_in_id]);

  const coilsInSelected = useMemo(() => selectedCoilInDoc?.coils || [], [selectedCoilInDoc]);

  const fetchRequests = async () => {
    try {
      const response = await api.programChangeRequests.list();
      setRequests(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch requests');
    }
  };

  const fetchCoilIns = async () => {
    try {
      const response = await api.coilIns.list({ is_active: true });
      setCoilIns(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch coil entries');
    }
  };

  const fetchProgramsForCoilIn = async (coilInId) => {
    try {
      const response = await api.programs.list({ coil_in_id: coilInId });
      const list = response.data || [];
      setPrograms(list);
    } catch (error) {
      toast.error('Failed to fetch programs');
    }
  };

  const handleCoilInChange = (coilInId) => {
    setFormData((prev) => ({
      ...prev,
      coil_in_id: coilInId,
      coil_id: '',
      current_program_id: '',
    }));
    setPrograms([]);
    if (coilInId) fetchProgramsForCoilIn(coilInId);
  };

  const handleCoilChange = (coilId) => {
    setFormData((prev) => ({
      ...prev,
      coil_id: coilId,
      current_program_id: '',
    }));
  };

  const programsForSelectedCoil = useMemo(() => {
    const cid = formData.coil_id;
    if (!cid) return [];
    const list = (programs || []).filter((p) => p?.coil_id === cid);

    // active first
    list.sort((a, b) => (b.is_active === true) - (a.is_active === true));
    return list;
  }, [programs, formData.coil_id]);

  const addCircleSize = () => {
    setFormData((prev) => ({
      ...prev,
      proposed_circle_sizes: [...prev.proposed_circle_sizes, ''],
    }));
  };

  const removeCircleSize = (index) => {
    setFormData((prev) => {
      const next = prev.proposed_circle_sizes.filter((_, i) => i !== index);
      return { ...prev, proposed_circle_sizes: next.length ? next : [''] };
    });
  };

  const updateCircleSize = (index, value) => {
    setFormData((prev) => {
      const next = [...prev.proposed_circle_sizes];
      next[index] = value;
      return { ...prev, proposed_circle_sizes: next };
    });
  };

  const resetForm = () => {
    setFormData({
      coil_in_id: '',
      coil_id: '',
      current_program_id: '',
      proposed_circle_sizes: [''],
      proposed_rate_per_kg: '',
      reason: '',
    });
    setPrograms([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canCreate) return toast.error('You do not have permission');
    if (!formData.coil_in_id) return toast.error('Please select a coil-in entry');
    if (!formData.coil_id) return toast.error('Please select a specific coil');

    const validSizes = formData.proposed_circle_sizes
      .map((s) => (s === '' ? null : Number(s)))
      .filter((n) => Number.isFinite(n) && n > 0);

    if (validSizes.length === 0) return toast.error('Please enter at least one valid circle size');

    const proposedRate = formData.proposed_rate_per_kg === '' ? null : Number(formData.proposed_rate_per_kg);
    if (proposedRate !== null && (!Number.isFinite(proposedRate) || proposedRate < 0)) {
      return toast.error('Proposed rate must be a valid non-negative number');
    }

    if (!formData.reason.trim()) return toast.error('Reason is required');

    setLoading(true);
    try {
      await api.programChangeRequests.create({
        coil_in_id: formData.coil_in_id,
        coil_id: formData.coil_id,
        current_program_id: formData.current_program_id || null,
        proposed_circle_sizes: validSizes,
        reason: formData.reason.trim(),
        proposed_rate_per_kg: proposedRate,
        proposed_rates_by_circle_size: null,
      });

      toast.success('Change request submitted (Pending approval)');
      setDialogOpen(false);
      resetForm();
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setLoading(false);
    }
  };

  const openApprovalDialog = (request) => {
    setSelectedRequest(request);
    setApprovalData({ status: 'APPROVED', notes: '' });
    setApprovalDialogOpen(true);
  };

  const handleApproval = async (e) => {
    e.preventDefault();
    if (!selectedRequest) return;

    setLoading(true);
    try {
      await api.programChangeRequests.approve(selectedRequest.id, {
        status: approvalData.status,
        notes: approvalData.notes?.trim() ? approvalData.notes.trim() : null,
      });

      toast.success(`Request ${approvalData.status.toLowerCase()}`);
      setApprovalDialogOpen(false);
      setSelectedRequest(null);
      setApprovalData({ status: 'APPROVED', notes: '' });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLed = (status) => {
    switch (status) {
      case 'PENDING':
        return (
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-[#F59E0B]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] shadow-[0_0_6px_#F59E0B] inline-block animate-pulse" /> PENDING
          </div>
        );
      case 'APPROVED':
        return (
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-[#10B981]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] shadow-[0_0_6px_#10B981] inline-block" /> APPROVED
          </div>
        );
      case 'REJECTED':
        return (
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-[#EF4444]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] shadow-[0_0_6px_#EF4444] inline-block" /> REJECTED
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-500 inline-block" /> {status}
          </div>
        );
    }
  };

  return (
    <div className="space-y-6 bg-[#141414] p-6 text-white min-h-screen" data-testid="program-change-requests-page">
      <div className="flex items-center justify-between border-b border-[#333333] pb-4">
        <div>
          <h1 className="font-mono text-3xl font-bold tracking-wider text-white uppercase">
            Program Change Requests
          </h1>
          <p className="text-slate-400 font-sans text-xs tracking-wider uppercase mt-1">
            Historical request logs and approval register
          </p>
        </div>

        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button
                onClick={resetForm}
                data-testid="create-request-button"
                className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-4 flex items-center justify-center gap-1.5 transition-colors"
              >
                <Plus className="h-4 w-4" />
                New Request
              </button>
            </DialogTrigger>

            <DialogContent className="max-w-2xl bg-[#1E1E1E] border border-[#333333] rounded-none text-white">
              <DialogHeader className="border-b border-[#333333] pb-3">
                <DialogTitle className="font-mono text-lg uppercase tracking-wider text-white">Create Change Request</DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                <div>
                  <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Select Coil-In Entry *</label>
                  <select
                    className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                    value={formData.coil_in_id}
                    onChange={(e) => handleCoilInChange(e.target.value)}
                    required
                    data-testid="coil-in-select"
                  >
                    <option value="">Select coil-in</option>
                    {coilIns.map((coil) => (
                      <option key={coil.id} value={coil.id}>
                        {coil.party_name || 'Unknown Party'} - {new Date(coil.inward_datetime).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.coil_in_id && (
                  <div>
                    <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Select Coil *</label>
                    <select
                      className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                      value={formData.coil_id}
                      onChange={(e) => handleCoilChange(e.target.value)}
                      required
                    >
                      <option value="">Select a coil</option>
                      {(coilsInSelected || []).map((c, idx) => (
                        <option key={c.coil_id || idx} value={c.coil_id}>
                          Coil #{idx + 1} - ID: {c.coil_id}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {programsForSelectedCoil.length > 0 && (
                  <div>
                    <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Current Program (Optional)</label>
                    <select
                      className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                      value={formData.current_program_id}
                      onChange={(e) => setFormData((prev) => ({ ...prev, current_program_id: e.target.value }))}
                    >
                      <option value="">None</option>
                      {programsForSelectedCoil.map((prog) => (
                        <option key={prog.id} value={prog.id}>
                          {(prog.circle_sizes || []).join(', ')}" {prog.is_active ? '(Active)' : '(Inactive)'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Proposed Circle Sizes (inches) *</label>
                  <div className="space-y-2 mt-2">
                    {formData.proposed_circle_sizes.map((size, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="number"
                          step="0.1"
                          placeholder="e.g., 8.0"
                          value={size}
                          onChange={(e) => updateCircleSize(index, e.target.value)}
                          className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                          data-testid={`proposed-size-${index}`}
                          required
                        />
                        {formData.proposed_circle_sizes.length > 1 && (
                          <button
                            type="button"
                            className="p-2 bg-[#2A2A2A] border border-[#333333] text-red-400 hover:bg-red-950/40 hover:border-red-900 rounded-none transition-colors"
                            onClick={() => removeCircleSize(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addCircleSize}
                    className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-xs uppercase tracking-wider h-9 px-3 flex items-center gap-1.5 mt-2 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5 text-slate-400" />
                    Add Size
                  </button>
                </div>

                <div>
                  <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Proposed Rate (₹/kg) (Optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="e.g., 2.50"
                    className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                    value={formData.proposed_rate_per_kg}
                    onChange={(e) => setFormData((prev) => ({ ...prev, proposed_rate_per_kg: e.target.value }))}
                  />
                </div>

                <div>
                  <label htmlFor="reason" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Reason *</label>
                  <textarea
                    id="reason"
                    className="w-full min-h-[80px] p-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] text-sm"
                    value={formData.reason}
                    onChange={(e) => setFormData((prev) => ({ ...prev, reason: e.target.value }))}
                    placeholder="Explain why this change is needed"
                    required
                  />
                </div>

                <div className="flex gap-2 justify-end pt-4 border-t border-[#333333]">
                  <button
                    type="button"
                    className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-6 flex items-center justify-center gap-1.5 transition-colors"
                    data-testid="submit-request"
                  >
                    {loading ? 'Creating...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="border-l-4 border-[#F59E0B] bg-[#1E1E1E] p-4 font-mono text-xs text-slate-300 space-y-1 rounded-none border border-y-[#333333] border-r-[#333333]">
        <span className="font-bold text-[#FF6B00] uppercase tracking-wider block">SYSTEM REGISTRY NOTE:</span>
        <p className="uppercase tracking-wide leading-relaxed">
          LEGACY PCR ARCHIVE FROZEN. ACTIVE PRODUCTION WORKFLOW IS ENTIRELY CONSOLIDATED ON CANONICAL PROGRAM PARAMETERS.
        </p>
      </div>

      <div className="bg-[#1E1E1E] border border-[#333333] rounded-none">
        <div className="p-4 border-b border-[#333333] bg-[#2A2A2A]/40">
          <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-white">All Request Logs</h2>
        </div>
        <div className="p-4">
          {requests.length === 0 ? (
            <div className="text-center py-8 font-mono text-xs text-slate-400">NO REQUESTS REGISTERED IN REGISTRY</div>
          ) : (
            <div className="divide-y divide-[#333333]/30">
              {requests.map((request) => (
                <div key={request.id} className="py-4 first:pt-0 last:pb-0 font-sans" data-testid={`request-${request.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2 text-xs">
                      <div className="flex items-center gap-3">
                        {getStatusLed(request.status)}
                        <span className="font-mono text-[10px] text-slate-400">
                          {new Date(request.created_at).toLocaleString()}
                        </span>
                      </div>

                      <p className="text-xs text-slate-300 leading-normal">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mr-2 block sm:inline-block">Proposed Sizes:</span>
                        <span className="font-mono font-bold text-white">{(request.proposed_circle_sizes || []).join(', ')}"</span>
                      </p>

                      <p className="text-xs text-slate-300 leading-normal">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mr-2 block sm:inline-block">Proposed Rate:</span>
                        <span className="font-mono font-bold text-[#FF6B00]">
                          {request.proposed_rate_per_kg === null || request.proposed_rate_per_kg === undefined
                            ? '-'
                            : `₹${request.proposed_rate_per_kg}/KG`}
                        </span>
                      </p>

                      <p className="text-xs text-slate-400 leading-relaxed">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mr-2 block">Reason for Change:</span>
                        <span className="bg-[#2A2A2A]/20 p-2 block border border-[#333333]/50 text-slate-300 mt-1">{request.reason}</span>
                      </p>

                      {request.approval_notes && (
                        <p className="text-xs text-slate-400 leading-relaxed border-l-2 border-[#FF6B00] bg-[#2A2A2A]/40 p-2 font-mono mt-2">
                          <span className="uppercase text-[9px] tracking-wider text-slate-500 block font-bold mb-0.5">Approval notes / findings:</span>
                          {request.approval_notes}
                        </p>
                      )}
                    </div>

                    {canApprove && request.status === 'PENDING' && (
                      <button
                        type="button"
                        onClick={() => openApprovalDialog(request)}
                        data-testid={`approve-request-${request.id}`}
                        className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-[10px] uppercase tracking-wider h-8 px-3 transition-colors flex-shrink-0"
                      >
                        Review
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="max-w-md bg-[#1E1E1E] border border-[#333333] rounded-none text-white">
          <DialogHeader className="border-b border-[#333333] pb-3">
            <DialogTitle className="font-mono text-lg uppercase tracking-wider text-white">Review Change Request</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleApproval} className="space-y-4 pt-4">
            {selectedRequest && (
              <div className="p-3 bg-[#2A2A2A] border border-[#333333] rounded-none text-xs space-y-2 font-mono">
                <p>
                  <span className="text-slate-400 uppercase tracking-wider text-[10px]">Proposed Sizes:</span>{' '}
                  <span className="text-white font-bold">{(selectedRequest.proposed_circle_sizes || []).join(', ')}"</span>
                </p>
                <p>
                  <span className="text-slate-400 uppercase tracking-wider text-[10px]">Proposed Rate:</span>{' '}
                  <span className="text-[#FF6B00] font-bold">
                    {selectedRequest.proposed_rate_per_kg === null || selectedRequest.proposed_rate_per_kg === undefined
                      ? '-'
                      : `₹${selectedRequest.proposed_rate_per_kg}/kg`}
                  </span>
                </p>
                <p>
                  <span className="text-slate-400 uppercase tracking-wider text-[10px] block">Reason:</span>
                  <span className="text-slate-300 block bg-[#1E1E1E] p-2 border border-[#333333] mt-1 leading-normal">{selectedRequest.reason}</span>
                </p>
              </div>
            )}

            <div>
              <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Decision *</label>
              <select
                className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                value={approvalData.status}
                onChange={(e) => setApprovalData((prev) => ({ ...prev, status: e.target.value }))}
                data-testid="approval-status-select"
              >
                <option value="APPROVED">Approve</option>
                <option value="REJECTED">Reject</option>
              </select>
            </div>

            <div>
              <label htmlFor="approval_notes" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Notes (Optional)</label>
              <textarea
                id="approval_notes"
                className="w-full min-h-[80px] p-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] text-sm"
                value={approvalData.notes}
                onChange={(e) => setApprovalData((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Add notes about your decision"
              />
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t border-[#333333]">
              <button
                type="button"
                className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                onClick={() => setApprovalDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-6 flex items-center justify-center gap-1.5 transition-colors"
                data-testid="confirm-approval"
              >
                {loading ? 'Processing...' : 'Confirm Decision'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramChangeRequests;
